import { FunctionComponent, useEffect, useState } from "react";
import Modal from "react-modal";
import styles from "./ColumnManager.module.css";
import { AiOutlineSearch } from "react-icons/ai";
import classNames from "classnames";
import { themeColors } from "../../theme";
import { connect } from "react-redux";
import "../../../theme-vars.css";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import "../../../theme-vars.css";
import { useMediaQuery } from "@material-ui/core";

// -------------------------------------------------------------
// Props Definition
// -------------------------------------------------------------
type props = {
  columnsList: any[];
  open: boolean;
  close: () => void;
  onChangeColumns?: (changeList: []) => void;
  toggleAllColumns?: any;
  theme: any;
};

// Global reference used during drag sorting
var row: any;

// -------------------------------------------------------------
// Column Manager Modal Component
// -------------------------------------------------------------
const ColumnManagerModal: FunctionComponent<props> = ({
  columnsList,
  open,
  close,
  onChangeColumns,
  toggleAllColumns,
  theme,
}) => {

  // Check if screen is mobile width
  const isMobile = useMediaQuery('(max-width: 900px)');

  // Search box text
  const [searchQuery, setsearchQuery] = useState("");

  // Working list of columns (used for reordering)
  const [sample, setSample] = useState<any[]>([]);

  // -------------------------------------------------------------
  // Initialize working column list whenever columnsList changes
  // -------------------------------------------------------------
  useEffect(() => {
    let data = [...columnsList];
    setSample(data);
  }, [columnsList]);

  // -------------------------------------------------------------
  // DRAG & DROP FUNCTIONS
  // Handle drag start and drag over for reordering items visually
  // -------------------------------------------------------------

  // Stores the dragged element reference
  const start = (event: any) => {
    row = event.target;
  };

  // Handles the reorder logic visually & updates local state
  const dragover = (event: any) => {
    var e = event;
    e.preventDefault();

    // Get all child nodes in the list container
    let children = Array.from(e.target.parentNode.parentNode.children);

    // Validation: ignore if number of children mismatch
    if (children.length === columnsList.length) {

      // Drag item moved downwards
      if (children.indexOf(e.target.parentNode) > children.indexOf(row)) {
        e.target.parentNode.after(row);
        const content = reorder(
          sample,
          children.indexOf(row),
          children.indexOf(e.target.parentNode)
        );
        setSample(content);
      } else {
        e.target.parentNode.before(row);
        const content = reorder(
          sample,

          children.indexOf(row),
          children.indexOf(e.target.parentNode)
        );
        setSample(content);
      }
    }
  };

  // Helper: reorder an array between two indices
  const reorder = (list: any, startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  // -------------------------------------------------------------
  // SAVE → returns the new column ordering to parent component
  // -------------------------------------------------------------
  const onSave = () => {
    let data: any = [];
    sample.map((item) => data.push(item.id));
    onChangeColumns && onChangeColumns(data);
    close();
  };

  // -------------------------------------------------------------
  // CANCEL → simply closes modal with no changes
  // -------------------------------------------------------------
  const onCancel = () => {
    close();
  };

  // -------------------------------------------------------------
  // RESET → reset to original ordering + visibility
  // -------------------------------------------------------------
  const onReset = () => {
    let data = [...columnsList];
    toggleAllColumns(false);
    setSample(data);
  };

  // -------------------------------------------------------------
  // Add animation class when modal is opened
  // -------------------------------------------------------------
  const filterClass = classNames(styles.parent, {
    [styles.showColumnAnim]: open === true,
  });

  // -------------------------------------------------------------
  // Custom styling for modal depending on theme
  // -------------------------------------------------------------
  const customStyles = {
    content: {
      backgroundColor: theme ? themeColors.dropdownBackgroundColor : themeColors.dropdownBackgroundColorDark, color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark
    },
  };

  // -------------------------------------------------------------
  // RENDER UI
  // -------------------------------------------------------------
  return (
    <Modal
      style={{ overlay: { zIndex: 202 }, ...customStyles }}
      isOpen={open}
      className={filterClass}
      overlayClassName={styles.overlay}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
    >
      {/* ---------------- HEADER ---------------- */}
      <div className={styles.headContainer}>
        <h6 style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-large)" }} className={styles.manageText}>Manage Columns</h6>
        {/* Search Input */}
        <div className={styles.searchBox}>
          <input
            className={styles.searchText}
            placeholder="Search"
            onChange={(val) => setsearchQuery(val.target.value)}
            style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}
          />
          <AiOutlineSearch />
        </div>
      </div>

      {/* ---------------- LIST OF COLUMNS ---------------- */}
      <div className={styles.listContainer}>
        {sample.map((item: any) => {
          if (item.Header.toLowerCase().includes(searchQuery.toLowerCase())) {
            return (
              <div
                key={item.id}
                className={styles.child}
                draggable={true}
                onDragStart={start}
                onDragOver={dragover}
              >
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  {...item.getToggleHiddenProps()}
                />
                <p style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }} className={styles.text}>{item.Header}</p>
              </div>
            );
          }
        })}
      </div>

      {/* ---------------- FOOTER BUTTONS ---------------- */}
      <div className={styles.buttonContainer}>
        <StyledCancelButton
          onClick={onCancel}
          minWidthAuto={isMobile ? true : false}
        >
          Cancel
        </StyledCancelButton>
        <StyledSubmitButton
          onClick={onReset}
          minWidthAuto={isMobile ? true : false}
        >
          Reset
        </StyledSubmitButton>
        <StyledSubmitButton
          onClick={onSave}
          minWidthAuto={isMobile ? true : false}
        >
          Save
        </StyledSubmitButton>
      </div>
    </Modal>
  );
};

// -------------------------------------------------------------
// Redux: Select required state into props
// -------------------------------------------------------------
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    organizationUsers: state.organizationUsers.data,
    role: state.user.role,
    organization: state.organization,
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(ColumnManagerModal);
